import { useAuthorization } from "@/hooks/use-authorization";
import { Permissions } from "@/utils/permissions";
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
} from "@elaniintc/goes-design-system";
import { DropdownMenuSeparator } from "@elaniintc/goes-design-system/ui";
import { NavLink } from "react-router";

interface EmailTemplateDropdownProps {
  id: string;
  status: string;
  hasContent: boolean;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onDeactivate: (id: string) => void;
  onActivate: (id: string) => void;
}

export default function EmailTemplateDropdown({
  id,
  status,
  hasContent,
  onEdit,
  onDuplicate,
  onDeactivate,
  onActivate,
}: EmailTemplateDropdownProps) {
  const { hasPermission } = useAuthorization();
  const canRead = hasPermission(Permissions["email-templates"].read);
  const canEdit = hasPermission(Permissions["email-templates"].update);
  const canDelete = hasPermission(Permissions["email-templates"].delete);

  if (!canRead && !canEdit && !canDelete) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="xs"
          className="size-8 cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
        >
          <Icon type="far" name="ellipsis-vertical" className="text-current" />
          <span className="sr-only">Acciones para plantilla {id}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-[222px] min-w-56 rounded-lg border-neutral text-body font-normal p-2 font-geist"
        side="bottom"
        align="end"
        sideOffset={4}
      >
        <DropdownMenuGroup>
          {canRead && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              asChild
            >
              <NavLink
                to={`/plantillas/correos/${id}`}
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                Ver plantilla
              </NavLink>
            </DropdownMenuItem>
          )}
          {canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onEdit(id);
              }}
            >
              Editar detalles
            </DropdownMenuItem>
          )}
          {canEdit && (
            <DropdownMenuItem
              className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onDuplicate(id);
              }}
            >
              Duplicar plantilla
            </DropdownMenuItem>
          )}
          {status === "active" && canDelete && (
            <>
              <DropdownMenuSeparator className="h-[1px] bg-[#B7C0CC] m-3" />
              <DropdownMenuItem
                className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onDeactivate(id);
                }}
              >
                Desactivar plantilla
              </DropdownMenuItem>
            </>
          )}
          {(status === "draft" || status === "inactive") &&
            hasContent &&
            canDelete && (
              <>
                <DropdownMenuSeparator className="h-[1px] bg-[#B7C0CC] m-3" />
                <DropdownMenuItem
                  className="hover:bg-neutral-object-accent-silent py-2 px-3 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    onActivate(id);
                  }}
                >
                  Activar plantilla
                </DropdownMenuItem>
              </>
            )}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
